import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "catalyst-ui-kit/dialog";
import {
  btnPrimaryCls,
  btnSecondaryCls,
  errorTextCls,
  inputCls,
  labelCls,
} from "components/tw/classes";
import { post as postProduct } from "containers/products/products.service";
import { fetch as fetchVats } from "features/vats/vats.service";
import { Product } from "types/Product";
import { suggestSku } from "./product-sku";

type Vat = { id: string; percent: number };

type Props = {
  companyId: string;
  /** What was typed in the product search; becomes the title. */
  initialTitle: string;
  onClose: () => void;
  /** The new product, shaped as a line the order can take. */
  onCreated: (product: Product) => void;
};

const PRICE_PATTERN = /^\d*\.?\d*$/;

/**
 * Makes a product or service without leaving the page that needs it. Asks for
 * what the products form requires (title, code, VAT) plus the price the line
 * starts with.
 */
export default function CreateProductDialog({
  companyId,
  initialTitle,
  onClose,
  onCreated,
}: Props) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initialTitle);
  const [sku, setSku] = useState(suggestSku(initialTitle));
  /** Once the code is typed by hand, editing the title stops rewriting it. */
  const [isSkuEdited, setIsSkuEdited] = useState(false);
  const [type, setType] = useState("product");
  const [price, setPrice] = useState("");
  const [vat, setVat] = useState("");
  const [vats, setVats] = useState<Vat[]>([]);
  const [saving, setSaving] = useState(false);
  /** Translation key of what went wrong, "" while nothing has. */
  const [errorKey, setErrorKey] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchVats({})
      .then(({ vatsCollection }: { vatsCollection: Vat[] }) => {
        if (!cancelled) setVats(vatsCollection);
      })
      .catch(() => {
        if (!cancelled) setErrorKey("error_getting_data");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!isSkuEdited) setSku(suggestSku(value));
  };

  const handlePriceChange = (value: string) => {
    const normalized = value.replace(",", ".");
    if (PRICE_PATTERN.test(normalized)) setPrice(normalized);
  };

  const canSave = !!title.trim() && !!sku.trim() && !!vat && !saving;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setErrorKey("");
    try {
      const { createdProduct } = await postProduct(companyId, {
        title: title.trim(),
        sku: sku.trim(),
        type,
        unitPrice: price,
        isActive: true,
        vat,
      });
      onCreated({
        product: createdProduct.id,
        title: createdProduct.title,
        sku: createdProduct.sku,
        price: price ? parseFloat(price) : "",
        quantity: 1,
      });
    } catch (e) {
      setErrorKey("not_saved");
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} size="lg">
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t("create_product")}</DialogTitle>
        <DialogBody className="space-y-4">
          <div>
            <label htmlFor="new-product-title" className={labelCls}>
              {t("title")}
            </label>
            <input
              id="new-product-title"
              className={inputCls}
              autoComplete="off"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="new-product-sku" className={labelCls}>
                {t("sku")}
              </label>
              <input
                id="new-product-sku"
                className={inputCls}
                autoComplete="off"
                value={sku}
                onChange={(e) => {
                  setSku(e.target.value);
                  setIsSkuEdited(true);
                }}
              />
            </div>
            <div>
              <label htmlFor="new-product-type" className={labelCls}>
                {t("type")}
              </label>
              <select
                id="new-product-type"
                className={inputCls}
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="product">{t("product")}</option>
                <option value="service">{t("service")}</option>
              </select>
            </div>
            <div>
              <label htmlFor="new-product-price" className={labelCls}>
                {t("price")}
              </label>
              <input
                id="new-product-price"
                className={inputCls}
                inputMode="decimal"
                autoComplete="off"
                value={price}
                onChange={(e) => handlePriceChange(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="new-product-vat" className={labelCls}>
                {t("vat")}
              </label>
              <select
                id="new-product-vat"
                className={inputCls}
                value={vat}
                onChange={(e) => setVat(e.target.value)}
              >
                <option value="">-</option>
                {vats.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.percent}%
                  </option>
                ))}
              </select>
            </div>
          </div>
          {errorKey && <p className={errorTextCls}>{t(errorKey)}</p>}
        </DialogBody>
        <DialogActions>
          <button type="button" className={btnSecondaryCls} onClick={onClose}>
            {t("cancel")}
          </button>
          <button type="submit" className={btnPrimaryCls} disabled={!canSave}>
            {t("save")}
          </button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
